import { allocateExclusive, allocateFlags } from '../allocate.js';
import { localePack } from '../locale/registry.js';
import { eraOf, type LocalePack, type Weighted, type WeightedName } from '../locale/types.js';
import { seededRng } from '../rng.js';
import { NICK_EN } from './nickname-en.js';
import type { MarketLocale } from '../../apps.js';
import type { Rng } from '../rng.js';

/**
 * Synthetic display names that read like the app's own users.
 *
 * Market-specific material comes from the app's locale pack; this engine only
 * decides how to combine it. Every style ratio is exact over the batch (see
 * `allocate.ts`), and a style a row cannot carry — a separator on a one-word
 * name, a number on a name that already ends in one — moves to a row that can,
 * so the count the operator asked for is the count they get.
 */

/** shuffly's gender values; generation itself only deals male/female. */
export type SyntheticGender = 'male' | 'female' | 'other';
export type NameMode = 'real' | 'nickname' | 'en-nickname';
export type CaseStyle = 'allLower' | 'firstCapOnly' | 'randomCase';
export type SepStyle = 'hyphenate' | 'underscore';

export interface NameGenConfig {
  modes: NameMode[];
  /** Share of `real` names that carry a surname. */
  surnameRatio: number;
  numberSuffix: { ratio: number; min: number; max: number };
  /** Ratio per enabled case style; one row takes at most one. Sum ≤ 1. */
  caseStyles: Partial<Record<CaseStyle, number>>;
  /** Ratio per enabled separator style; one row takes at most one. Sum ≤ 1. */
  sepStyles: Partial<Record<SepStyle, number>>;
  /** Independent ratio of rows written in leetspeak. */
  leet: number;
}

export interface NameSubject {
  gender: 'male' | 'female';
  birthYear: number;
}

export function defaultNameGenConfig(): NameGenConfig {
  return {
    modes: ['real'],
    surnameRatio: 1,
    numberSuffix: { ratio: 0, min: 1, max: 99 },
    caseStyles: {},
    sepStyles: {},
    leet: 0,
  };
}

/** How a name was built — tests assert the spread of these. */
export type NamePattern =
  | 'real'
  | 'name+initial'
  | 'name.initial'
  | 'vowel-drop'
  | 'doubled'
  | 'name+yy'
  | 'name+yyyy'
  | 'name+place'
  | 'word'
  | 'adj+noun'
  | 'en-word'
  | 'en-adj+noun';

export function generateNames(
  config: NameGenConfig,
  subjects: NameSubject[],
  locale: MarketLocale,
  seed?: string | null,
  pack: LocalePack = localePack(locale),
): string[] {
  return generateNameDetails(pack, config, subjects, seed).map((d) => d.name);
}

export function generateNameDetails(
  pack: LocalePack,
  config: NameGenConfig,
  subjects: NameSubject[],
  seed?: string | null,
): { name: string; pattern: NamePattern }[] {
  const rng = seededRng(seed);
  const n = subjects.length;
  const modes: NameMode[] = config.modes.length > 0 ? config.modes : ['real'];
  const withSurname = allocateFlags(n, config.surnameRatio, rng);

  const built = subjects.map((s, i) => buildTokens(pack, pick(modes, rng), s, withSurname[i] === true, rng));

  const seps = reassign(allocateExclusive(n, config.sepStyles, rng), (i) => separable(built[i]));
  const cases = allocateExclusive(n, config.caseStyles, rng);
  const leet = allocateFlags(n, config.leet, rng);
  const numbers = reassign(
    allocateFlags(n, config.numberSuffix.ratio, rng).map((f) => (f ? true : null)),
    (i) => !/\d$/.test(built[i]?.tokens.at(-1) ?? ''),
  );

  const compose = (b: Built, i: number): string => {
    const style = seps[i];
    const sep = style === 'hyphenate' ? '-' : style === 'underscore' ? '_' : null;
    // A separator style owns every break in the name, the ones inside a
    // first name too: "Lea-Sophie" under underscores is "Lea_Sophie_…".
    let name = sep === null ? b.tokens.join(b.join) : b.tokens.map((t) => t.replace(/[-\s]+/g, sep)).join(sep);
    // Handles are ASCII, so their case changes must not be Turkish-aware:
    // tr-TR would turn "irem" into "İrem" and "Ice" into "ıce".
    name = applyCase(name, cases[i] ?? null, b.pattern === 'real' ? pack : ASCII_CASE, rng);
    if (leet[i] === true) name = applyLeet(name, pack);
    if (numbers[i] === true) name += String(randInt(config.numberSuffix.min, config.numberSuffix.max, rng));
    return name.trim();
  };

  // Names are unique per batch under the username column's comparison
  // (case- and accent-insensitive): commit refuses the second of a pair, so
  // a duplicate here only turns into a blocked commit later.
  const seen = new Set<string>();
  return built.map((first, i) => {
    let b = first;
    let name = compose(b, i);
    const subject = subjects[i];
    for (let attempt = 0; subject !== undefined && attempt < 8 && seen.has(nameKey(name)); attempt++) {
      b = buildTokens(pack, pick(modes, rng), subject, withSurname[i] === true, rng);
      name = compose(b, i);
    }
    while (seen.has(nameKey(name))) name = `${name}${String(randInt(10, 99, rng))}`;
    seen.add(nameKey(name));
    return { name, pattern: b.pattern };
  });
}

/** Locale-neutral case for ASCII handles. */
const ASCII_CASE = { lower: (s: string) => s.toLowerCase(), upper: (s: string) => s.toUpperCase() };

/** Can a separator style land on this name: two tokens, or a break inside one. */
function separable(b: Built | undefined): boolean {
  return b !== undefined && (b.tokens.length >= 2 || b.tokens.some((t) => /[-\s]/.test(t)));
}

/** How the username column compares names: case and accents ignored. */
function nameKey(name: string): string {
  return name
    .replace(/İ/g, 'i')
    .toLowerCase()
    .replace(/ı/g, 'i')
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

interface Built {
  tokens: string[];
  /** How the tokens join when no separator style is assigned. */
  join: string;
  pattern: NamePattern;
}

const LOCAL_PATTERNS: readonly [NamePattern, number][] = [
  ['name+initial', 3],
  ['name.initial', 2],
  ['vowel-drop', 1],
  ['doubled', 1],
  ['name+yy', 2],
  ['name+yyyy', 1],
  ['name+place', 2],
  ['word', 2],
  ['adj+noun', 2],
];

function buildTokens(pack: LocalePack, mode: NameMode, s: NameSubject, surname: boolean, rng: Rng): Built {
  if (mode === 'en-nickname') {
    return rng.next() < 0.5
      ? { tokens: [pick(NICK_EN.words, rng)], join: '', pattern: 'en-word' }
      : { tokens: [pick(NICK_EN.adjectives, rng), pick(NICK_EN.nouns, rng)], join: '', pattern: 'en-adj+noun' };
  }

  const first = pickFirstName(pack, s, rng);
  if (mode === 'real') {
    const tokens = [first];
    if (surname) tokens.push(weightedPick(pack.names.surnames, rng).name);
    return { tokens, join: ' ', pattern: 'real' };
  }

  // Local nickname: what people type, so folded to ASCII and lowercase.
  const handle = (x: string): string => pack.lower(pack.asciiFold(x)).replace(/[^a-z0-9]/g, '');
  const base = handle(first.split(/[\s'-]/)[0] ?? first) || handle(first);
  const initial = handle(weightedPick(pack.names.surnames, rng).name).slice(0, 1) || 'x';
  const pattern = weightedPick(LOCAL_PATTERNS.map(([name, weight]) => ({ name, weight })), rng).name;
  switch (pattern) {
    case 'name+initial':
      return { tokens: [base, initial], join: '', pattern };
    case 'name.initial':
      return { tokens: [base, initial], join: '.', pattern };
    case 'vowel-drop': {
      const dropped = base.slice(0, 1) + base.slice(1).replace(/[aeiou]/g, '');
      return { tokens: [dropped.length >= 3 ? dropped : base], join: '', pattern };
    }
    case 'doubled':
      return { tokens: [base + base.slice(-1)], join: '', pattern };
    case 'name+yy':
      return { tokens: [base, String(s.birthYear % 100).padStart(2, '0')], join: '', pattern };
    case 'name+yyyy':
      return { tokens: [base, String(s.birthYear)], join: '', pattern };
    case 'name+place':
      return { tokens: [base, handle(weightedPick(pack.handle.placeCodes, rng).name)], join: '', pattern };
    case 'word':
      return { tokens: [handle(pick(pack.handle.words, rng))], join: '', pattern };
    default:
      return {
        tokens: [capitalize(pack, pick(pack.handle.adjectives, rng)), capitalize(pack, pick(pack.handle.nouns, rng))]
          .map((t) => pack.asciiFold(t).replace(/[^A-Za-z0-9]/g, '')),
        join: '',
        pattern: 'adj+noun',
      };
  }
}

/** Popularity-weighted, restricted to the subject's birth cohort when any name fits it. */
function pickFirstName(pack: LocalePack, s: NameSubject, rng: Rng): string {
  const all = s.gender === 'female' ? pack.names.female : pack.names.male;
  const era = eraOf(s.birthYear);
  const fit = all.filter((n: WeightedName) => n.eras.includes(era));
  return weightedPick(fit.length > 0 ? fit : all, rng).name;
}

/**
 * Move each assignment a row cannot carry to an unassigned row that can.
 * An assignment with nowhere to go is dropped rather than forced.
 */
function reassign<T>(assigned: (T | null)[], eligible: (i: number) => boolean): (T | null)[] {
  const out = [...assigned];
  const free = out.map((v, i) => (v === null && eligible(i) ? i : -1)).filter((i) => i >= 0);
  for (let i = 0; i < out.length; i++) {
    const v = out[i];
    if (v === null || v === undefined || eligible(i)) continue;
    out[i] = null;
    const to = free.shift();
    if (to !== undefined) out[to] = v;
  }
  return out;
}

function applyCase(
  name: string,
  style: CaseStyle | null,
  pack: Pick<LocalePack, 'lower' | 'upper'>,
  rng: Rng,
): string {
  switch (style) {
    case 'allLower':
      return pack.lower(name);
    case 'firstCapOnly': {
      const l = pack.lower(name);
      return l === '' ? l : pack.upper(l.charAt(0)) + l.slice(1);
    }
    case 'randomCase':
      return [...name].map((c) => (rng.next() < 0.5 ? pack.lower(c) : pack.upper(c))).join('');
    default:
      return name;
  }
}

const LEET: Readonly<Record<string, string>> = { a: '4', e: '3', i: '1', o: '0', s: '5' };

export function applyLeet(s: string, pack: LocalePack): string {
  return [...s].map((c) => LEET[pack.lower(c)] ?? c).join('');
}

function capitalize(pack: LocalePack, s: string): string {
  return s === '' ? s : pack.upper(s.charAt(0)) + s.slice(1);
}

function pick<T>(arr: readonly T[], rng: Rng): T {
  return arr[Math.floor(rng.next() * arr.length)] as T;
}

function weightedPick<T extends Weighted | WeightedName | { name: string; weight: number }>(arr: readonly T[], rng: Rng): T {
  const total = arr.reduce((s, x) => s + x.weight, 0);
  let r = rng.next() * total;
  for (const x of arr) {
    r -= x.weight;
    if (r < 0) return x;
  }
  return arr[arr.length - 1]!;
}

function randInt(min: number, max: number, rng: Rng): number {
  const lo = Math.min(min, max);
  const hi = Math.max(min, max);
  return lo + Math.floor(rng.next() * (hi - lo + 1));
}
